import { useMemo } from 'react';

import cloneDeep from 'lodash.clonedeep';

import { AVAILABILITIES_FILTERS } from '../components/business/availability/list/AvailabilitiesListFilters';
import { LOCAL_STORAGE_PRECART, LOCAL_STORAGE_PRECART_DEFVAL } from '../constants';
import { getUniqueSessionGroupIds } from '../utils/business/availabilities';
import { getTotalParticipants } from '../utils/business/variants';
import useReadFilters from './useReadFilters';
import useWriteStorageBookingEngine from './useWriteStorageBookingEngine';
import { updateSeveralVariantsPrices } from './useWriteVariantsPrices';

const useWritePreCart = () => {
  const [preCartData, setPreCartData] = useWriteStorageBookingEngine(LOCAL_STORAGE_PRECART, LOCAL_STORAGE_PRECART_DEFVAL);

  const { debouncedFilters, getFilterValue } = useReadFilters();
  const nbTicketFilter = AVAILABILITIES_FILTERS.NB_TICKETS;
  const filteredNbTickets = useMemo(
    () => getFilterValue(nbTicketFilter.fieldName, nbTicketFilter.type, nbTicketFilter.default),
    [JSON.stringify(debouncedFilters)]
  );

  const setPreCartProduct = data => {
    setPreCartData({ product: data, itemsToDeleteForEdit: null, canUseSameSessionShortcut: false });
  };

  const setPreCartShopResource = (shopResource, shopResourceChildren) => {
    setPreCartData({
      shopResource: shopResource,
      shopResourceChildren: shopResourceChildren,
      itemsToDeleteForEdit: null,
      canUseSameSessionShortcut: false,
    });
  };

  const setPreCartAvailabilities = data => {
    setPreCartData({ ...preCartData, availabilities: data });
  };

  const setPreCartVariant = (sessionGroupId, sameSessionIndex, data, newDefaultVariant) => {
    const copyAvailabilities = cloneDeep(preCartData.availabilities);

    // Same session booking shortcut : variants data is not shared between availabilities
    if (sameSessionIndex != null) {
      copyAvailabilities[sameSessionIndex].variants = data;
    } else {
      for (const availability of copyAvailabilities) {
        if (availability.session_group_id !== sessionGroupId) continue;

        availability.variants = data;
        break;
      }
    }

    if (newDefaultVariant) {
      setPreCartData({ ...preCartData, availabilities: copyAvailabilities, defaultVariant: newDefaultVariant });
    } else {
      setPreCartAvailabilities(copyAvailabilities);
    }
  };

  const setPreCartDefaultVariant = (defaultVariant, loggedUser, product) => {
    const copyAvailabilities = cloneDeep(preCartData.availabilities);

    if (loggedUser) {
      createSingleVariant(copyAvailabilities, defaultVariant);
    } else {
      createSeveralVariants(copyAvailabilities, defaultVariant);
    }

    const newPreCartData = {
      ...preCartData,
      availabilities: copyAvailabilities,
      defaultVariant: defaultVariant,
      itemsToDeleteForEdit: null,
      canUseSameSessionShortcut: false,
    };
    if (product) {
      newPreCartData['product'] = product;
    }

    // If only a single session has been selected (in price per session)
    // Then we will allow a shortcut on P3 to book the same session several times
    const hasSingleSession = getUniqueSessionGroupIds(copyAvailabilities).length === 1;
    if (hasSingleSession && !newPreCartData['product'].price_per_participant) {
      newPreCartData['canUseSameSessionShortcut'] = true;
    }

    setPreCartData(newPreCartData);
  };

  const createSingleVariant = (availabilities, defaultVariant) => {
    for (const availability of availabilities) {
      const firstVariant = cloneDeep(defaultVariant);
      firstVariant.participant = filteredNbTickets ?? 1;

      fillAvailabilityWithVariantData(availability, defaultVariant, [firstVariant]);
    }
  };

  const createSeveralVariants = (availabilities, defaultVariant) => {
    const maxVariantsToCreate = filteredNbTickets ?? 1;

    for (const availability of availabilities) {
      // We do this to not overbook, the nb_tickets is calculated across all sessions
      const actualMaxVariantsToCreate = availability.max_participants
        ? Math.min(availability.max_participants, maxVariantsToCreate)
        : maxVariantsToCreate;

      const availabilityVariants = [];

      for (let i = 1; i <= actualMaxVariantsToCreate; i++) {
        availabilityVariants.push(cloneDeep(defaultVariant));
      }

      // We added several variants so we have to update the prices, in case the discount price is available
      if (actualMaxVariantsToCreate > 1) {
        updateSeveralVariantsPrices(availabilityVariants, getTotalParticipants(availabilityVariants));
      }

      fillAvailabilityWithVariantData(availability, defaultVariant, availabilityVariants);
    }
  };

  const fillAvailabilityWithVariantData = (availability, defaultVariant, availabilityVariants) => {
    availability.variants = availabilityVariants;
    availability.nb_sessions = defaultVariant.nbSessions;

    // for direct access to P3
    if (defaultVariant.start_date && !availability.start_date) {
      availability.start_date = defaultVariant.start_date;
    }
  };

  const addPreCartSameSession = () => {
    if (!preCartData.availabilities.length) return;

    const avaCopy = cloneDeep(preCartData.availabilities[0]);
    avaCopy._key = Math.random(); // for react ui iteration, we need a way to differenciate items in a rendered list
    fillAvailabilityWithVariantData(avaCopy, preCartData.defaultVariant, [cloneDeep(preCartData.defaultVariant)]);

    const copyAvailabilities = cloneDeep(preCartData.availabilities);
    copyAvailabilities.push(avaCopy);
    setPreCartAvailabilities(copyAvailabilities);
  };

  const removePreCartSameSession = indexToRemove => {
    const copyAvailabilities = cloneDeep(preCartData.availabilities);
    copyAvailabilities.splice(indexToRemove, 1);
    setPreCartAvailabilities(copyAvailabilities);
  };

  return {
    preCartData,
    setPreCartProduct,
    setPreCartShopResource,
    setPreCartAvailabilities,
    setPreCartVariant,
    setPreCartDefaultVariant,
    setPreCartData,
    // AvailabilityCopy is called when using the shortcut on P3 to add the same session again
    addPreCartSameSession,
    removePreCartSameSession,
  };
};

export default useWritePreCart;
